import * as React from 'react'

import useBooleanState from '../hooks/useBooleanState'
import downloadAttachment, {
  downloadFileLegacy,
} from '../services/download-file'
import OnLoading from '../components/renderer/OnLoading'
import { FormTypes } from '@oneblink/types'
import FormElementLabelContainer from '../components/renderer/FormElementLabelContainer'
import drawTimestampOnCanvas from '../services/drawTimestampOnCanvas'
import { FormElementBinaryStorageValue } from '../types/attachments'
import useAttachment from '../hooks/attachments/useAttachment'
import AnnotationModal, {
  superimposeAnnotationOnImage,
} from '../components/renderer/AnnotationModal'
import Modal from '../components/renderer/Modal'
import {
  checkIfContentTypeIsImage,
  prepareNewAttachment,
  correctFileOrientation,
} from '../services/attachments'
import AttachmentStatus from '../components/renderer/attachments/AttachmentStatus'
import { canvasToBlob, urlToBlobAsync } from '../services/blob-utils'
import ImagePreviewUnavailable from '../components/renderer/attachments/ImagePreviewUnavailable'
import { FormElementValueChangeHandler, IsDirtyProps } from '../types/form'
import ProgressBar from '../components/renderer/attachments/ProgressBar'
import useElementAriaDescribedby from '../hooks/useElementAriaDescribedby'
import MaterialIcon from '../components/MaterialIcon'
import FormElementValidationMessage from '../components/renderer/FormElementValidationMessage'
import CropModal from '../components/ImageCropper/CropModal'
import { generateCroppedImageBlob } from '../components/ImageCropper'
import { PercentCrop } from 'react-image-crop'

type Props = {
  id: string
  element: FormTypes.CameraElement
  value: FormElementBinaryStorageValue
  onChange: FormElementValueChangeHandler<FormElementBinaryStorageValue>
  displayValidationMessage: boolean
  validationMessage: string | undefined
} & IsDirtyProps

function FormElementCamera({
  id,
  element,
  value,
  onChange,
  validationMessage,
  displayValidationMessage,
  isDirty,
  setIsDirty,
}: Props) {
  const ariaDescribedby = useElementAriaDescribedby(id, element)
  const [{ cameraError, isLoading }, setState] = React.useState<{
    isLoading: boolean
    cameraError?: Error
  }>({
    isLoading: false,
  })
  const [isAnnotating, setIsAnnotating, clearIsAnnotating] =
    useBooleanState(false)
  const [isCropping, setIsCropping, clearIsCropping] = useBooleanState(false)

  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const clearImage = React.useCallback(() => {
    onChange(element, {
      value: undefined,
    })
  }, [element, onChange])

  const fileChange = React.useCallback(
    async (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
      if (!changeEvent.target || !changeEvent.target.files) {
        return
      }

      const file = changeEvent.target.files[0]
      if (!file) {
        return
      }

      setState({
        isLoading: true,
      })

      console.log('File selected event', file)
      try {
        if (!file.size) {
          throw new Error('You cannot upload an empty file.')
        }

        if (!checkIfContentTypeIsImage(file.type)) {
          throw new Error(
            `Invalid file type "${file.type}". Please select an image.`,
          )
        }
        const result = await correctFileOrientation(
          file,
          element.includeTimestampWatermark ? drawTimestampOnCanvas : undefined,
        )

        if (result instanceof Blob) {
          onChange(element, {
            value: prepareNewAttachment(result, file.name, element),
          })
        } else {
          const blob = await canvasToBlob(result)
          onChange(element, {
            value: prepareNewAttachment(blob, file.name, element),
          })
        }

        setIsDirty()
        setState({
          isLoading: false,
        })
      } catch (error) {
        setState({
          isLoading: false,
          cameraError: error as Error,
        })
      }
    },
    [element, onChange, setIsDirty],
  )
  const openCamera = React.useCallback(() => {
    if (window.cordova && navigator.camera && navigator.camera.getPicture) {
      setState({
        isLoading: true,
      })
      navigator.camera.getPicture(
        (base64Data: string) => {
          urlToBlobAsync(`data:image/jpeg;base64,${base64Data}`)
            .then((blob) => {
              onChange(element, {
                value: prepareNewAttachment(blob, 'photo.jpeg', element),
              })
              setState({
                isLoading: false,
              })
            })
            .catch((error) => {
              setState({
                cameraError: error,
                isLoading: false,
              })
            })
        },
        (error: Error) => {
          console.warn(
            'An error occurred while attempting to take a photo',
            error,
          )
          setState({
            isLoading: false,
            cameraError: error,
          })
        },
        {
          quality: 100,
          destinationType: window.Camera.DestinationType.DATA_URL,
          sourceType: window.Camera.PictureSourceType.CAMERA,
          allowEdit: false,
          encodingType: window.Camera.EncodingType.JPEG,
          mediaType: window.Camera.MediaType.PICTURE,
          correctOrientation: true,
          saveToPhotoAlbum: false,
          cameraDirection: window.Camera.Direction.BACK,
        },
      )
    } else if (fileInputRef.current) {
      // RESET HTML FILE INPUT VALUE SO FILES PREVIOUSLY ADDED AND REMOVED ARE RECOGNIZED
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    } else {
      console.error(
        'Could not find "input" element in Camera component template',
      )
    }
  }, [element, onChange])

  const {
    isUploading,
    uploadErrorMessage,
    isLoadingAttachmentUrl,
    attachmentUrl,
    loadAttachmentUrlError,
    canDownload,
    progress,
    contentType,
  } = useAttachment(
    value,
    element,
    React.useCallback(
      (id, attachment) => {
        onChange(element, {
          value: attachment,
        })
      },
      [element, onChange],
    ),
  )

  const handleRetry = React.useMemo(() => {
    if (!value || typeof value !== 'object') return

    if (value.type === 'ERROR' && value.data) {
      return () => {
        onChange(element, {
          value: {
            type: 'NEW',
            _id: value._id,
            data: value.data,
            fileName: value.fileName,
            isPrivate: value.isPrivate,
          },
        })
      }
    }
  }, [element, onChange, value])

  const handleDownload = React.useCallback(async () => {
    if (typeof value === 'string') {
      await downloadFileLegacy(value, id)
    } else if (value && value.type !== 'ERROR') {
      await downloadAttachment(value)
    }
  }, [value, id])

  const handleSaveAnnotation = React.useCallback(
    async (annotationDataUri: string) => {
      clearIsAnnotating()

      if (typeof attachmentUrl !== 'string') {
        return
      }

      setState({
        isLoading: true,
      })
      try {
        const blob = await superimposeAnnotationOnImage({
          annotationDataUri,
          attachmentUrl,
        })
        setState({
          isLoading: false,
        })
        if (blob) {
          const attachment = prepareNewAttachment(blob, 'photo.png', element)
          onChange(element, {
            value: attachment,
          })
        }
      } catch (err) {
        setState({
          cameraError: err as Error,
          isLoading: false,
        })
      }
    },
    [attachmentUrl, clearIsAnnotating, element, onChange],
  )

  const handleSaveCrop = React.useCallback(
    async (croppedAreaPercent: PercentCrop) => {
      clearIsCropping()
      if (!attachmentUrl) return

      setState({
        isLoading: true,
      })

      try {
        const croppedImage = await generateCroppedImageBlob({
          croppedAreaPercent,
          imgSrc: attachmentUrl,
          fileType: contentType,
        })

        if (!croppedImage) throw new Error('Cropped image is null')
        const attachment = prepareNewAttachment(
          croppedImage,
          'photo.png',
          element,
        )
        onChange(element, {
          value: attachment,
        })
        setState({
          isLoading: false,
        })
      } catch (error) {
        setState({
          cameraError: error as Error,
          isLoading: false,
        })
      }
    },
    [attachmentUrl, clearIsCropping, contentType, element, onChange],
  )

  const progressTooltipRef = React.useRef<HTMLDivElement>(null)
  return (
    <>
      <FormElementLabelContainer
        className="ob-camera"
        element={element}
        id={id}
        required={element.required}
      >
        <div className="control">
          {(value || isLoading) && (
            <>
              <figure className="ob-figure" ref={progressTooltipRef}>
                <DisplayImage
                  isUploading={isUploading}
                  uploadErrorMessage={uploadErrorMessage}
                  isLoadingAttachmentUrl={isLoadingAttachmentUrl}
                  attachmentUrl={attachmentUrl}
                  loadAttachmentUrlError={loadAttachmentUrlError}
                  isLoading={isLoading}
                  element={element}
                  onAnnotate={setIsAnnotating}
                  onCrop={setIsCropping}
                  canDownload={canDownload}
                  progress={progress}
                />
                <ProgressBar isShowing={isUploading} progress={progress} />
              </figure>
            </>
          )}

          <input
            ref={fileInputRef}
            className="ob-input ob-camera__input-hidden cypress-camera-control"
            type="file"
            accept="image/*"
            capture="environment"
            id={id}
            name={element.name}
            required={element.required}
            disabled={element.readOnly}
            onChange={fileChange}
            aria-required={element.required}
          />
          <div className="buttons ob-buttons">
            {value ? (
              <>
                {uploadErrorMessage && handleRetry && (
                  <button
                    type="button"
                    className="button ob-button ob-button__retry is-light cypress-retry-file-button"
                    onClick={handleRetry}
                  >
                    Retry
                  </button>
                )}
                <button
                  type="button"
                  className="button ob-button ob-button__clear is-light cypress-clear-camera"
                  onClick={clearImage}
                  disabled={element.readOnly || isLoading}
                >
                  Clear
                </button>
                {canDownload && (
                  <button
                    type="button"
                    className="button ob-button ob-button__download is-primary cypress-download-file-button"
                    onClick={handleDownload}
                  >
                    <span className="icon">
                      <MaterialIcon>cloud_download</MaterialIcon>
                    </span>
                    <span>&nbsp;Download</span>
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                className="button ob-button ob-button__open is-primary cypress-open-camera"
                onClick={openCamera}
                disabled={element.readOnly || isLoading}
                aria-describedby={ariaDescribedby}
                onBlur={setIsDirty}
              >
                Open Camera
              </button>
            )}
          </div>
        </div>
        {(isDirty || displayValidationMessage) && !!validationMessage && (
          <FormElementValidationMessage message={validationMessage} />
        )}
      </FormElementLabelContainer>

      {isAnnotating && attachmentUrl && (
        <AnnotationModal
          imageSrc={attachmentUrl}
          onClose={clearIsAnnotating}
          onSave={handleSaveAnnotation}
        />
      )}

      {isCropping && attachmentUrl && (
        <CropModal
          imageSrc={attachmentUrl}
          onClose={clearIsCropping}
          onSave={handleSaveCrop}
        />
      )}

      {cameraError && (
        <Modal
          isOpen
          title="Whoops..."
          className="cypress-error-modal"
          titleClassName="cypress-error-title"
          actions={
            <button
              type="button"
              className="button ob-button is-primary cypress-close-error-button"
              onClick={() => setState({ isLoading: false })}
              autoFocus
            >
              Okay
            </button>
          }
        >
          <p>
            An error occurred while attempting to take a photo. Please click{' '}
            <b>Okay</b> below to try again. If the problem persists, please
            contact support.
          </p>

          <div className="content has-margin-top-6">
            <blockquote>{cameraError.toString()}</blockquote>
          </div>
        </Modal>
      )}
    </>
  )
}

export default React.memo(FormElementCamera)

const DisplayImage = React.memo(function DisplayImage({
  uploadErrorMessage,
  isUploading,
  isLoadingAttachmentUrl,
  attachmentUrl,
  loadAttachmentUrlError,
  isLoading,
  element,
  onAnnotate,
  onCrop,
  progress,
}: Omit<ReturnType<typeof useAttachment>, 'contentType'> & {
  element: FormTypes.CameraElement
  isLoading: boolean
  onAnnotate: () => void
  onCrop: () => void
  progress: number | undefined
}) {
  if (uploadErrorMessage) {
    return (
      <div className="figure-content" role="alert">
        <h3 className="title is-3">Upload Failed</h3>
        <p>
          Your photo failed to upload, please use the <b>Retry</b> or{' '}
          <b>Clear</b> buttons below.
        </p>
      </div>
    )
  }

  if (loadAttachmentUrlError) {
    return (
      <div className="figure-content" role="alert">
        <h3 className="title is-3">Preview Failed</h3>
        <p>{loadAttachmentUrlError.message}</p>
      </div>
    )
  }

  if (isLoadingAttachmentUrl || isLoading) {
    return (
      <div className="figure-content has-text-centered cypress-camera-loading-image">
        <OnLoading small />
      </div>
    )
  }

  if (attachmentUrl) {
    return (
      <>
        <span className="ob-figure__status">
          <AttachmentStatus
            isLoadingAttachmentUrl={isLoadingAttachmentUrl}
            loadAttachmentUrlError={loadAttachmentUrlError}
            isUploading={isUploading}
            attachmentUrl={attachmentUrl}
            progress={progress}
          />
        </span>
        <img
          src={attachmentUrl}
          className="cypress-camera-image ob-camera__img"
          crossOrigin="anonymous"
          alt={`${element.label}: Attachment`}
        />
        <div className="ob-image-file__actions">
          <button
            type="button"
            className="button is-primary ob-camera__crop-button cypress-crop-button"
            onClick={onCrop}
            disabled={element.readOnly}
          >
            <span className="icon">
              <MaterialIcon>crop</MaterialIcon>
            </span>
          </button>
          <button
            type="button"
            className="button is-primary ob-camera__annotate-button cypress-annotate-button"
            onClick={onAnnotate}
            disabled={element.readOnly}
          >
            <span className="icon">
              <MaterialIcon>brush</MaterialIcon>
            </span>
          </button>
        </div>
      </>
    )
  }

  return (
    <div className="figure-content" role="alert">
      <ImagePreviewUnavailable />
    </div>
  )
})
